import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges, Input, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';

import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { InventoryService } from '../../services/inventory.service';
import { PrintService } from '../../services/print.service'
import { ReportsService } from '../../services/reports.service'

@Component({
	selector: 'app-trending-products',
	templateUrl: './trending-products.component.html',
	styleUrls: ['./trending-products.component.scss']
})
export class TrendingProductsComponent implements OnInit, OnChanges, OnDestroy {
@Input() customer = null;
@Input() vendor = null;
@Input() menuapp = false;
@ViewChild('print_table') printtable: ElementRef;
searchForm = null;
categories: any = [];
productlines: any = [];
filename: any = 'Top_100_Sales_';
brands: any = [];
user: any = null;
results: any = [];
salespeople: any = [];
sending = false;
loading = false;
loadError = '';
reportResults: any = [];
sortedReportResults: any = [];
sortByColumn: string;
sortDirection: string;
summary: any = [];
color: any = 'blue';
showDetails = false;
private userSub: Subscription;

private static readonly THEME_PALETTE: Record<string, { light: [number, number, number]; base: [number, number, number]; dark: [number, number, number] }> = {
	purple:  { light: [206, 147, 216], base: [156,  39, 176], dark: [ 74,  20, 140] },
	primary: { light: [206, 147, 216], base: [156,  39, 176], dark: [ 74,  20, 140] },
	blue:    { light: [144, 202, 249], base: [ 33, 150, 243], dark: [ 25, 118, 210] },
	info:    { light: [128, 222, 234], base: [  0, 188, 212], dark: [  0,  96, 100] },
	green:   { light: [165, 214, 167], base: [ 76, 175,  80], dark: [ 27,  94,  32] },
	success: { light: [165, 214, 167], base: [ 76, 175,  80], dark: [ 27,  94,  32] },
	orange:  { light: [255, 204, 128], base: [255, 152,   0], dark: [230,  81,   0] },
	warning: { light: [255, 224, 130], base: [255, 193,   7], dark: [255, 143,   0] },
	red:     { light: [239, 154, 154], base: [244,  67,  54], dark: [183,  28,  28] },
	danger:  { light: [239, 154, 154], base: [244,  67,  54], dark: [183,  28,  28] },
	rose:    { light: [244, 143, 177], base: [233,  30,  99], dark: [136,  14,  79] },
};

get palette() {
	return TrendingProductsComponent.THEME_PALETTE[this.color] || TrendingProductsComponent.THEME_PALETTE['blue'];
}

get themeVars(): { [key: string]: string } {
	const p = this.palette;
	const rgb  = (c: number[]) => `rgb(${c[0]},${c[1]},${c[2]})`;
	const rgba = (c: number[], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
	return {
		'--tp-accent':        rgb(p.base),
		'--tp-accent-dark':   rgb(p.dark),
		'--tp-accent-light':  rgb(p.light),
		'--tp-accent-tint':   rgba(p.base, 0.07),
		'--tp-accent-tint-2': rgba(p.base, 0.14),
		'--tp-accent-border': rgba(p.base, 0.20),
		'--tp-accent-bg':     rgba(p.light, 0.16),
	};
}

constructor(private reportService: ReportsService, private printService: PrintService, private fb: UntypedFormBuilder, private inventoryService: InventoryService, private globalSearchService: GlobalSearchService) {}

ngOnInit(): void {
	this.color = 'success';

	this.userSub = this.globalSearchService.user.subscribe(result => {
		this.user = result;
		if (!result?.user?.userid) {
			return;
		}
		this.ensureSearchForm();
		this.getData();
	});

	this.inventoryService.getItemCategories().subscribe(c => {
		this.categories = c
	});
	this.inventoryService.getItemProductlines().subscribe(p => {
		this.productlines = p
	});

	this.inventoryService.getBrands().subscribe((results: any) => {
		this.brands = results;
	});
	this.reportService.getSalesPeople().subscribe((results: any) => {
		this.salespeople = results;
	});
}

ngOnChanges(changes: SimpleChanges): void {
	if ((changes['customer'] || changes['vendor']) && this.searchForm) {
		this.searchForm.patchValue({
			customer: this.customer,
			vendor: this.vendor,
			weeks: this.getTrendWeeks(),
		}, { emitEvent: false });
		if (this.user?.user?.userid) {
			this.getData();
		}
	}
}

ngOnDestroy(): void {
	if (this.userSub) {
		this.userSub.unsubscribe();
	}
}

getTrendWeeks(): number {
	return this.customer?.debtorno ? 26 : 8;
}

private ensureSearchForm(): void {
	if (!this.user?.user?.userid) {
		return;
	}
	if (this.searchForm) {
		this.searchForm.patchValue({
			customer: this.customer,
			vendor: this.vendor,
			user: this.user.user.userid,
			weeks: this.getTrendWeeks(),
			limit: 100,
		}, { emitEvent: false });
		return;
	}
	this.searchForm = this.fb.group({
		customer: this.customer,
		vendor: this.vendor,
		limit: 100,
		weeks: this.getTrendWeeks(),
		user: this.user.user.userid,
	});
}

getData() {
	if (!this.searchForm || !this.user?.user?.userid) {
		return;
	}
	this.sending = true;
	this.loading = true;
	this.loadError = '';
	this.globalSearchService.aiDataAvailable$.next(null);

	this.inventoryService.customerTrendingProducts(this.searchForm.value).subscribe({
		next: (p: any) => {
			this.loading = false;
			this.sending = false;
			this.reportResults = Array.isArray(p?.results) ? p.results : [];
			this.summary = p?.summary || {};
			this.sortedReportResults = [...this.reportResults];
			setTimeout(() => {
				this.sendCustomerTrendToAI(this.customer, this.reportResults);
			}, 100);
		},
		error: () => {
			this.loading = false;
			this.sending = false;
			this.loadError = 'Unable to load sales trends.';
			this.reportResults = [];
			this.sortedReportResults = [];
			this.summary = {};
		},
	});
}

toggleDetails(): void {
	this.showDetails = !this.showDetails;
}
	
private sendCustomerTrendToAI(customer: any, trendingResults: any[]): void {
	  if (!customer || !trendingResults || trendingResults.length === 0) {return;}
	
	  let topBrands: string[] = [];
	  if (customer?.topthree && Array.isArray(customer.topthree) && customer.topthree.length > 0) {
		topBrands = customer.topthree
		  .map((b: any) => b?.name)
		  .filter((name: string) => name && name.trim() !== '');
	  }
	
	  if (topBrands.length === 0) {
		topBrands = this.calculateBrandsFromTrends(trendingResults);
	  }
	
	  const brandStats = {};
	  let totalSpend = 0;
	  let totalQty = 0;
	  let returnQty = 0;
	  const recentWeeks = 12;
	  const now = new Date();
	
	  const enrichedTrends = trendingResults.slice(0, 30).map(item => {
		const brand = item.brand || item.stockid?.substring(0, 2) || 'Unknown';
		totalSpend += item.salestotal || 0;
		totalQty += item.qtytotal || 0;
		if (item.qtytotal < 0) {returnQty += Math.abs(item.qtytotal);}
	
		if (!brandStats[brand]) {brandStats[brand] = 0;}
		brandStats[brand] += item.qtytotal || 0;
	
		const recentSales = (item.weekly_sales || []).slice(-recentWeeks);
	
		const trend: any = {
		  stockid: item.stockid,
		  description: item.description?.substring(0, 100) || '',
		  brand: brand,
		  qtytotal: Math.round((item.qtytotal || 0) * 100) / 100,
		  salestotal: Math.round((item.salestotal || 0) * 100) / 100,
		  avg_price: Math.round((item.avg_price || 0) * 100) / 100,
		  avg_weekly: Math.round((item.avg_weekly || 0) * 100) / 100,
		  frequency: item.frequency || 0,
		  growth_rate_pct: Math.round((item.growth_rate_pct || 0) * 100) / 100,
		  volatility: Math.round((item.volatility || 0) * 100) / 100,
		  slope: Math.round((item.slope || 0) * 100) / 100,
		  one_time_spike: item.one_time_spike || false,
		  recent_sales: recentSales.map((s: number) => Math.round(s))
		};
		
		Object.keys(trend).forEach(key => {
			if (trend[key] === null || trend[key] === undefined || trend[key] === '') {
				delete trend[key];
			}
		});
		
		return trend;
	  });
	 
	 const uniqueProductCount = enrichedTrends.length;
	 const avgMonthlyQty = totalQty ? +(Number(totalQty) / 6).toFixed(2) : 0;
	 const avgMonthlySpend = totalSpend ? +(Number(totalSpend) / 6).toFixed(2) : 0;
	 const returnRate = totalQty > 0 ? +(Number(returnQty) / Number(totalQty) * 100).toFixed(1) : 0;
	 
	 const lifetimeValue = this.calculateLifetimeValue(customer, totalSpend);
	 const purchaseFrequency = this.calculatePurchaseFrequency(enrichedTrends);
	 const seasonalTrends = this.analyzeSeasonalTrends(enrichedTrends);
	 const productRecommendations = this.identifyProductOpportunities(enrichedTrends, topBrands);
	 
	  const aiPayload: any = {
		customer_profile: {
		  id: customer.customer_id || customer.id || null,
		  name: (customer.name || customer.customerName || '').substring(0, 100),
		  location: (customer.location || customer.address || '').substring(0, 100),
		  email: customer.email || '',
		  phone: customer.phone || '',
		  assigned_salesrep: customer.salesrep || customer.rep || '',
		  last_order_date: customer.last_order_date || '',
		  total_unique_products: uniqueProductCount,
		  average_monthly_quantity: parseFloat(avgMonthlyQty.toString()),
		  average_monthly_spend: parseFloat(avgMonthlySpend.toString()),
		  return_rate_pct: parseFloat(returnRate.toString()),
		  top_brands: topBrands
		},
		trends: enrichedTrends,
	  };

	  if (lifetimeValue) {
		aiPayload.customer_profile.lifetimeValue = lifetimeValue;
	  }

	  if (purchaseFrequency) {
		aiPayload.purchaseFrequency = purchaseFrequency;
	  }

	  if (seasonalTrends) {
		aiPayload.seasonalTrends = seasonalTrends;
	  }

	  if (productRecommendations) {
		aiPayload.productRecommendations = productRecommendations;
	  }
	  
	  Object.keys(aiPayload.customer_profile).forEach(key => {
		if (aiPayload.customer_profile[key] === null || aiPayload.customer_profile[key] === undefined || aiPayload.customer_profile[key] === '') {
			delete aiPayload.customer_profile[key];
		}
	  });
	
	  console.log('📊 AI Trend Payload:\n', JSON.stringify(aiPayload, null, 2));
	
	  this.globalSearchService.aiDataAvailable$.next(aiPayload);
	  this.globalSearchService.aiDataContext$.next('customer_trend');
}

exportPdf() {
	const now = new Date();
	const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
	const data = {
		content: encoded,
		filename: this.filename + (now.getMonth() + 1) + now.getDate() + now.getFullYear(),
		title: `Trending Products - Created @ ${now.toLocaleString()}`,
		subtitle: this.getReportSubtitle()
	}

	this.printService.pdf(data).subscribe((result: any) => {
		this.globalSearchService.downloadPdf(result.content, data.filename);
	});
}

exportXls() {
	const now = new Date();
	const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
	const data = {
		content: encoded,
		filename: this.filename + (now.getMonth() + 1) + now.getDate() + now.getFullYear(),
		title: `Trending Products - Created @ ${now.toLocaleString()}`,
		subtitle: this.getReportSubtitle()
	}

	this.printService.xls(data).subscribe((result: any) => {
		this.globalSearchService.downloadXls(result.content, data.filename);
	});
}

getReportSubtitle() {
	let out = '';

	if (this.customer) {

		out += ' for Customer ' + this.customer.customer.brname;

	} else if (this.vendor) {
		out += ' for Vendor ' + this.vendor.name;
	}
	return out;
}

sortBy(property: string): void {
	if (this.sortByColumn === property) {
		// Toggle sort direction if the same column is clicked
		this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
	} else {
		// Default to ascending order when a new column is clicked
		this.sortDirection = 'asc';
		this.sortByColumn = property;
	}

	this.sortedReportResults.sort((a, b) => {
		// Check if the property values are numeric
		const isNumeric = !isNaN(parseFloat(a[property])) && isFinite(a[property]) && !isNaN(parseFloat(b[property])) && isFinite(b[property]);

		if (isNumeric) {
			// Sort as numbers if the property values are numeric
			const numA = parseFloat(a[property]);
			const numB = parseFloat(b[property]);
			return this.sortDirection === 'asc' ? numA - numB : numB - numA;
		} else {
			// Sort as strings if the property values are not numeric
			const comparison = a[property].localeCompare(b[property]);
			return this.sortDirection === 'asc' ? comparison : -comparison;
		}
	});
}


sortAscending(data: any[], property: string): any[] {
	return data.sort((a, b) => {
		if (a[property] < b[property]) {return -1;}
		if (a[property] > b[property]) {return 1;}
		return 0;
	});
}

sortDescending(data: any[], property: string): any[] {
	return data.sort((a, b) => {
		if (a[property] > b[property]) {return -1;}
		if (a[property] < b[property]) {return 1;}
		return 0;
	});
}

getSortIcon(property: string): string {
	if (this.sortByColumn !== property) {
		return 'fa-sort text-muted';
	}
	return this.sortDirection === 'asc' ? 'fa-sort-up text-success' : 'fa-sort-down text-danger';
}

getTotalSales(): number {
	if (!this.reportResults || !Array.isArray(this.reportResults)) {
		return 0;
	}
	return this.reportResults.reduce((sum, item) => sum + (item.salestotal || 0), 0);
}

getTotalQuantity(): number {
	if (!this.reportResults || !Array.isArray(this.reportResults)) {
		return 0;
	}
	return this.reportResults.reduce((sum, item) => sum + (item.qtytotal || 0), 0);
}

getAveragePrice(): number {
	const totalSales = this.getTotalSales();
	const totalQty = this.getTotalQuantity();
	return totalQty > 0 ? totalSales / totalQty : 0;
}

getGrowingTrendsCount(): number {
	if (!this.reportResults || !Array.isArray(this.reportResults)) {
		return 0;
	}
	return this.reportResults.filter(r => r.slope > 0).length;
}

getTrialSpikesCount(): number {
	if (!this.reportResults || !Array.isArray(this.reportResults)) {
		return 0;
	}
	return this.reportResults.filter(r => r.one_time_spike).length;
}

getStableTrendsCount(): number {
	if (!this.reportResults || !Array.isArray(this.reportResults)) {
		return 0;
	}
	return this.reportResults.filter(r => r.slope >= -0.5 && r.slope <= 0.5 && !r.one_time_spike).length;
}

private calculateBrandsFromTrends(trendingResults: any[]): string[] {
	const brandStats = {};
	trendingResults.forEach(item => {
		const brand = item.brand || item.stockid?.substring(0, 2) || 'Unknown';
		if (!brandStats[brand]) {brandStats[brand] = 0;}
		brandStats[brand] += item.qtytotal || 0;
	});
	return Object.entries(brandStats)
		.sort(([, aQty], [, bQty]) => (Number(bQty) || 0) - (Number(aQty) || 0))
		.slice(0, 3)
		.map(([brand]) => brand);
}

private calculateLifetimeValue(customer: any, totalSpend: number): any {
	if (!customer || !totalSpend) {return null;}

	const customerSince = customer.customer_since || customer.created_date;
	if (!customerSince) {return null;}

	const sinceDate = new Date(customerSince);
	const now = new Date();
	const yearsAsCustomer = (now.getTime() - sinceDate.getTime()) / (1000 * 60 * 60 * 24 * 365);
		
	if (yearsAsCustomer <= 0) {return null;}

	const annualValue = totalSpend / yearsAsCustomer;
	const projectedLifetimeValue = annualValue * 10;

	return {
		totalSpend: Math.round(totalSpend * 100) / 100,
		yearsAsCustomer: Math.round(yearsAsCustomer * 10) / 10,
		annualValue: Math.round(annualValue * 100) / 100,
		projectedLifetimeValue: Math.round(projectedLifetimeValue * 100) / 100,
		valueTier: annualValue > 10000 ? 'High' : annualValue > 5000 ? 'Medium' : 'Standard'
	};
}

private calculatePurchaseFrequency(trends: any[]): any {
	if (!trends || trends.length === 0) {return null;}

	const activeTrends = trends.filter(t => t.frequency > 0);
	if (activeTrends.length === 0) {return null;}

	const avgFrequency = activeTrends.reduce((sum, t) => sum + (t.frequency || 0), 0) / activeTrends.length;
	const consistentBuyers = activeTrends.filter(t => t.frequency >= 5).length;
	const occasionalBuyers = activeTrends.filter(t => t.frequency >= 2 && t.frequency < 5).length;

	return {
		averageFrequency: Math.round(avgFrequency * 100) / 100,
		consistentBuyersCount: consistentBuyers,
		occasionalBuyersCount: occasionalBuyers,
		pattern: avgFrequency >= 5 ? 'Frequent' : avgFrequency >= 2 ? 'Regular' : 'Occasional'
	};
}

private analyzeSeasonalTrends(trends: any[]): any {
	if (!trends || trends.length === 0) {return null;}

	const growingTrends = trends.filter(t => t.slope > 0.5);
	const decliningTrends = trends.filter(t => t.slope < -0.5);
	const stableTrends = trends.filter(t => t.slope >= -0.5 && t.slope <= 0.5);

	const seasonalItems = trends.filter(t => {
		const recentSales = t.recent_sales || [];
		if (recentSales.length < 4) {return false;}
		const firstHalf = recentSales.slice(0, Math.floor(recentSales.length / 2));
		const secondHalf = recentSales.slice(Math.floor(recentSales.length / 2));
		const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
		const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
		return Math.abs(secondAvg - firstAvg) / firstAvg > 0.3;
	});

	return {
		growingTrendsCount: growingTrends.length,
		decliningTrendsCount: decliningTrends.length,
		stableTrendsCount: stableTrends.length,
		seasonalItemsCount: seasonalItems.length,
		hasSeasonalPattern: seasonalItems.length > 0
	};
}

private identifyProductOpportunities(trends: any[], topBrands: string[]): any {
	if (!trends || trends.length === 0) {return null;}

	const opportunities = [];
		
	const growingItems = trends
		.filter(t => t.slope > 1 && t.frequency >= 2)
		.slice(0, 5)
		.map(t => ({
			stockid: t.stockid,
			description: t.description,
			growthRate: t.growth_rate_pct,
			reason: 'Strong growth trend'
		}));

	if (growingItems.length > 0) {
		opportunities.push({
			type: 'Growing_Products',
			items: growingItems,
			recommendation: 'Consider increasing inventory or promoting these items'
		});
	}

	const brandOpportunities = topBrands.slice(0, 3).map(brand => ({
		brand: brand,
		itemsInTrend: trends.filter(t => t.brand === brand).length,
		recommendation: `Explore additional products from ${brand} brand`
	}));

	if (brandOpportunities.length > 0) {
		opportunities.push({
			type: 'Brand_Expansion',
			brands: brandOpportunities
		});
	}

	return opportunities.length > 0 ? { opportunities } : null;
}

getTrendBadgeClass(slope: number): string {
	if (slope > 1) { return 'tp-badge--up-strong'; }
	if (slope > 0.5) { return 'tp-badge--up'; }
	if (slope > -0.5) { return 'tp-badge--neutral'; }
	if (slope > -1) { return 'tp-badge--down'; }
	return 'tp-badge--down-strong';
}

getTrendIcon(slope: number): string {
	if (slope > 0) {return 'fa-arrow-up';}
	if (slope < 0) {return 'fa-arrow-down';}
	return 'fa-minus';
}

getVolatilityBadgeClass(volatility: number): string {
	if (volatility < 0.3) { return 'tp-badge--vol-low'; }
	if (volatility < 0.6) { return 'tp-badge--vol-mid'; }
	if (volatility < 1.0) { return 'tp-badge--vol-high'; }
	return 'tp-badge--vol-extreme';
}

getRowClass(item: any): string {
	if (item.one_time_spike) {return 'table-warning';}
	if (item.slope > 1) {return 'table-success';}
	if (item.slope < -1) {return 'table-danger';}
	return '';
}

}
